import { memo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import { Layout } from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useClaimLink } from "../hooks/useClaimLink";
import { useConsumedLinkGuard } from "../hooks/useConsumedLinkGuard";
import { createCashDepositLink, getWidgetApiError, isDeadLinkError } from "../services/api";
import { APP_ROUTES } from "../constants";
import { isHttpUrl, returnToChat } from "../utils/returnToChat";
import {
  appendAnalyticsHandoff,
  getAnalyticsSession,
  getEvents,
  safeCapture,
} from "../analytics";

/**
 * Post-create cash-deposit launcher. Mints a dedicated cash-deposit link
 * (unique mint guard) and hands off. Chat hears `operation.created` separately.
 */
export const CashDepositPage = memo(() => {
  useConsumedLinkGuard();
  useClaimLink();
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const colorConfig = theme.whiteView;
  const operationId = searchParams.get("operationId");
  const linkId = session.data?.linkId;
  const returnToChatUrl = session.data?.config?.returnToChatUrl;

  const startedRef = useRef(false);

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("barcode"));
  }, []);

  useEffect(() => {
    if (startedRef.current || !linkId) return;

    if (!operationId) {
      startedRef.current = true;
      if (isHttpUrl(returnToChatUrl)) {
        returnToChat(returnToChatUrl);
        return;
      }
      navigate(APP_ROUTES.SUMMARY_PAGE, { replace: true });
      return;
    }

    startedRef.current = true;

    const callbackUrl = isHttpUrl(returnToChatUrl)
      ? returnToChatUrl!
      : `${window.location.origin}${APP_ROUTES.SUMMARY_PAGE}/?linkId=${linkId}`;

    (async () => {
      try {
        const { url } = await createCashDepositLink(
          linkId,
          operationId,
          callbackUrl,
        );
        const { sessionId, distinctId } = getAnalyticsSession();
        const handoffUrl =
          sessionId && distinctId
            ? appendAnalyticsHandoff(url, { sessionId, distinctId })
            : url;
        window.location.href = handoffUrl;
      } catch (error) {
        console.error(
          "[CashDepositPage] Failed to create cash-deposit link:",
          error,
        );
        safeCapture(getEvents().BARCODE_CREATED, {
          success: false,
          error_code: "CASH_DEPOSIT_LINK_FAILED",
          operation_id: operationId,
        });

        if (isDeadLinkError(error)) {
          return;
        }

        if (isHttpUrl(returnToChatUrl)) {
          returnToChat(returnToChatUrl);
          return;
        }

        const maintenance = destinationMaintenanceCopy(error);
        navigate(APP_ROUTES.ERROR_PAGE, {
          state: {
            title: maintenance?.title ?? null,
            subTitle: null,
            message: maintenance?.body ?? null,
            btnText: t("cashDeposit.back"),
            isOut: false,
            pageUrl: APP_ROUTES.SUMMARY_PAGE,
          },
        });
      }
    })();
  }, [linkId, operationId, navigate, t, returnToChatUrl]);

  return (
    <AuraLayout colorConfig={colorConfig}>
      <Layout.Content>
        <div className="landing-section content-center loader-page">
          <section className="content">
            <div
              className="loader-spinner"
              role="status"
              aria-label="Loading"
            />
            <BodyCopy>{t("cashDeposit.preparing")}</BodyCopy>
          </section>
        </div>
      </Layout.Content>
    </AuraLayout>
  );
});

function destinationMaintenanceCopy(
  error: unknown,
): { title: string; body: string } | null {
  const api = getWidgetApiError(error);
  if (api?.code !== "FLOW_UNAVAILABLE_MAINTENANCE") return null;
  const details = (api.details ?? {}) as { title?: string; body?: string };
  const title = details.title?.trim() ?? "";
  const body = details.body?.trim() ?? "";
  if (!title && !body) return null;
  return { title, body };
}
